import { createClient } from '@supabase/supabase-js'

// Get environment variables - NO FALLBACKS for security
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. ' +
    'Please create a .env file with VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY. ' +
    'See .env.example for reference.'
  )
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    storageKey: 'tsa-auth-token',
    autoRefreshToken: true,
    detectSessionInUrl: true
  }
})

// Get current environment
export const getCurrentEnvironment = () => {
  return import.meta.env.VITE_ENV || 'staging'
}

// Types för Supabase
export type Database = {
  public: {
    Tables: {
      articles: {
        Row: {
          id: string
          article_number: string
          name: string
          description: string | null
          is_rut_applicable: boolean
          is_provision_article: boolean
          category: string | null
          type: 'product' | 'service' | 'hourly'
          unit: string
          vat_rate: number
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          article_number?: string
          name: string
          description?: string | null
          is_rut_applicable?: boolean
          is_provision_article?: boolean
          category?: string | null
          type?: 'product' | 'service' | 'hourly'
          unit?: string
          vat_rate?: number
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          article_number?: string
          name?: string
          description?: string | null
          is_rut_applicable?: boolean
          is_provision_article?: boolean
          category?: string | null
          type?: 'product' | 'service' | 'hourly'
          unit?: string
          vat_rate?: number
          created_at?: string
          updated_at?: string
        }
      }
      customers: {
        Row: {
          id: string
          full_name: string
          email: string
          phone: string | null
          address_street: string
          address_city: string
          address_postal_code: string
          address_country: string
          notes: string | null
          active: boolean
          price_list_id: string | null
          provision_partner_id: string | null
          fortnox_customer_id: string | null
          wants_rut: boolean
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          full_name: string
          email: string
          phone?: string | null
          address_street: string
          address_city: string
          address_postal_code: string
          address_country?: string
          notes?: string | null
          active?: boolean
          price_list_id?: string | null
          provision_partner_id?: string | null
          fortnox_customer_id?: string | null
          wants_rut?: boolean
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          full_name?: string
          email?: string
          phone?: string | null
          address_street?: string
          address_city?: string
          address_postal_code?: string
          address_country?: string
          notes?: string | null
          active?: boolean
          price_list_id?: string | null
          provision_partner_id?: string | null
          fortnox_customer_id?: string | null
          wants_rut?: boolean
          created_at?: string
          updated_at?: string
        }
      }
      customer_rut_applicants: {
        Row: {
          id: string
          customer_id: string
          name: string
          personal_number: string
          sort_order: number
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          customer_id: string
          name: string
          personal_number: string
          sort_order?: number
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          customer_id?: string
          name?: string
          personal_number?: string
          sort_order?: number
          created_at?: string
          updated_at?: string
        }
      }
      user_profiles: {
        Row: {
          id: string
          user_id: string
          email: string
          full_name: string
          role: 'admin' | 'subcontractor'
          phone: string | null
          company: string | null
          is_active: boolean
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          user_id: string
          email: string
          full_name: string
          role: 'admin' | 'subcontractor'
          phone?: string | null
          company?: string | null
          is_active?: boolean
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          email?: string
          full_name?: string
          role?: 'admin' | 'subcontractor'
          phone?: string | null
          company?: string | null
          is_active?: boolean
          created_at?: string
          updated_at?: string
        }
      }
      subcontractors: {
        Row: {
          id: string
          user_id: string | null
          name: string
          email: string
          phone: string | null
          color: string
          is_active: boolean
          is_admin: boolean
          company: string | null
          location: string | null
          org_number: string | null
          fortnox_supplier_id: string | null
          hourly_rate: number | null
          notes: string | null
          address_street: string | null
          address_postal_code: string | null
          address_city: string | null
          address_country: string | null
          price_list_id: string | null
          previous_assignments_count: number | null
          available_days: boolean[] | null  // [Mon, Tue, Wed, Thu, Fri, Sat, Sun]
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          user_id?: string | null
          name: string
          email: string
          phone?: string | null
          color?: string
          is_active?: boolean
          is_admin?: boolean
          company?: string | null
          location?: string | null
          org_number?: string | null
          fortnox_supplier_id?: string | null
          hourly_rate?: number | null
          notes?: string | null
          address_street?: string | null
          address_postal_code?: string | null
          address_city?: string | null
          address_country?: string | null
          price_list_id?: string | null
          previous_assignments_count?: number | null
          available_days?: boolean[] | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          user_id?: string | null
          name?: string
          email?: string
          phone?: string | null
          color?: string
          is_active?: boolean
          is_admin?: boolean
          company?: string | null
          location?: string | null
          org_number?: string | null
          fortnox_supplier_id?: string | null
          hourly_rate?: number | null
          notes?: string | null
          address_street?: string | null
          address_postal_code?: string | null
          address_city?: string | null
          address_country?: string | null
          price_list_id?: string | null
          previous_assignments_count?: number | null
          available_days?: boolean[] | null
          created_at?: string
          updated_at?: string
        }
      }
      assignments: {
        Row: {
          id: string
          title: string
          description: string | null
          start_date: string
          start_time: string
          end_time: string
          status: string
          customer_id: string
          location: string | null
          recurring_type: 'none' | 'daily' | 'weekly' | 'biweekly' | 'monthly'
          estimated_hours: number | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          title: string
          description?: string | null
          start_date: string
          start_time: string
          end_time: string
          status?: string
          customer_id: string
          location?: string | null
          recurring_type?: 'none' | 'daily' | 'weekly' | 'biweekly' | 'monthly'
          estimated_hours?: number | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          title?: string
          description?: string | null
          start_date?: string
          start_time?: string
          end_time?: string
          status?: string
          customer_id?: string
          location?: string | null
          recurring_type?: 'none' | 'daily' | 'weekly' | 'biweekly' | 'monthly'
          estimated_hours?: number | null
          created_at?: string
          updated_at?: string
        }
      }
      assignment_subcontractors: {
        Row: {
          id: string
          assignment_id: string
          subcontractor_id: string
          created_at: string
        }
        Insert: {
          id?: string
          assignment_id: string
          subcontractor_id: string
          created_at?: string
        }
        Update: {
          id?: string
          assignment_id?: string
          subcontractor_id?: string
          created_at?: string
        }
      }
      assignment_articles: {
        Row: {
          id: string
          assignment_id: string
          article_id: string
          quantity: number
          custom_price: number | null
          notes: string | null
          exclude_from_tg_reporting: boolean
          subcontractor_prices: Record<string, number>
          subcontractor_quantities: Record<string, number>  // Per-TG quantities
          created_at: string
        }
        Insert: {
          id?: string
          assignment_id: string
          article_id: string
          quantity?: number
          custom_price?: number | null
          notes?: string | null
          exclude_from_tg_reporting?: boolean
          subcontractor_prices?: Record<string, number>
          subcontractor_quantities?: Record<string, number>
          created_at?: string
        }
        Update: {
          id?: string
          assignment_id?: string
          article_id?: string
          quantity?: number
          custom_price?: number | null
          notes?: string | null
          exclude_from_tg_reporting?: boolean
          subcontractor_prices?: Record<string, number>
          subcontractor_quantities?: Record<string, number>
          created_at?: string
        }
      }
      price_lists: {
        Row: {
          id: string
          name: string
          description: string | null
          type: 'customer' | 'subcontractor'
          is_default: boolean
          is_active: boolean
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          name: string
          description?: string | null
          type: 'customer' | 'subcontractor'
          is_default?: boolean
          is_active?: boolean
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          name?: string
          description?: string | null
          type?: 'customer' | 'subcontractor'
          is_default?: boolean
          is_active?: boolean
          created_at?: string
          updated_at?: string
        }
      }
      price_list_articles: {
        Row: {
          id: string
          price_list_id: string
          article_id: string
          price_excl_vat: number
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          price_list_id: string
          article_id: string
          price_excl_vat: number
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          price_list_id?: string
          article_id?: string
          price_excl_vat?: number
          created_at?: string
          updated_at?: string
        }
      }
      article_categories: {
        Row: {
          id: string
          name: string
          description: string | null
          color: string
          sort_order: number
          is_active: boolean
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          name: string
          description?: string | null
          color?: string
          sort_order?: number
          is_active?: boolean
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          name?: string
          description?: string | null
          color?: string
          sort_order?: number
          is_active?: boolean
          created_at?: string
          updated_at?: string
        }
      }
      provision_partners: {
        Row: {
          id: string
          name: string
          email: string | null
          percentage: number
          is_active: boolean
          notes: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          name: string
          email?: string | null
          percentage?: number
          is_active?: boolean
          notes?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          name?: string
          email?: string | null
          percentage?: number
          is_active?: boolean
          notes?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      provision_payouts: {
        Row: {
          id: string
          provision_partner_id: string
          period_year: number
          period_month: number
          amount: number
          invoice_number: string | null
          invoice_received_at: string | null
          notes: string | null
          created_at: string
        }
        Insert: {
          id?: string
          provision_partner_id: string
          period_year: number
          period_month: number
          amount: number
          invoice_number?: string | null
          invoice_received_at?: string | null
          notes?: string | null
          created_at?: string
        }
        Update: {
          id?: string
          provision_partner_id?: string
          period_year?: number
          period_month?: number
          amount?: number
          invoice_number?: string | null
          invoice_received_at?: string | null
          notes?: string | null
          created_at?: string
        }
      }
      subcontractor_unavailability: {
        Row: {
          id: string
          subcontractor_id: string
          start_date: string
          end_date: string
          reason: string | null
          created_by: string | null
          created_at: string
        }
        Insert: {
          id?: string
          subcontractor_id: string
          start_date: string
          end_date: string
          reason?: string | null
          created_by?: string | null
          created_at?: string
        }
        Update: {
          id?: string
          subcontractor_id?: string
          start_date?: string
          end_date?: string
          reason?: string | null
          created_by?: string | null
          created_at?: string
        }
      }
      driving_log_vehicles: {
        Row: {
          id: string
          name: string
          registration_number: string
          created_by: string | null
          is_active: boolean
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          name: string
          registration_number: string
          created_by?: string | null
          is_active?: boolean
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          name?: string
          registration_number?: string
          created_by?: string | null
          is_active?: boolean
          created_at?: string
          updated_at?: string
        }
      }
      driving_log_entries: {
        Row: {
          id: string
          vehicle_id: string
          driver_id: string | null
          date: string
          odometer_start: number
          odometer_end: number | null
          from_address: string
          to_address: string
          purpose: string | null
          assignment_id: string | null
          km: number | null
          notes: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          vehicle_id: string
          driver_id?: string | null
          date: string
          odometer_start: number
          odometer_end?: number | null
          from_address: string
          to_address: string
          purpose?: string | null
          assignment_id?: string | null
          notes?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          vehicle_id?: string
          driver_id?: string | null
          date?: string
          odometer_start?: number
          odometer_end?: number | null
          from_address?: string
          to_address?: string
          purpose?: string | null
          assignment_id?: string | null
          notes?: string | null
          created_at?: string
          updated_at?: string
        }
      }
    }
  }
}
